import api from '../../../api/axios';
import { API_ENDPOINTS } from '../../../api/endpoints';
import {
  SCREEN_ACTIONS,
  type MyScreenAccess,
  type RoleAccessUpdate,
  type ScreenAccessMatrix,
  type ScreenAction,
  type ScreenKey,
} from '../model/types';

/** The Spring API answers { IsSuccess, StatusCode, Message, Data1 }; this returns Data1. */
const unwrapApi = <T,>(data: unknown): T => {
  const row = (data ?? {}) as Record<string, unknown>;
  const ok = row.IsSuccess ?? row.isSuccess;
  if (ok === false) {
    throw new Error(String(row.Message ?? row.message ?? 'Request failed'));
  }
  return (row.Data1 ?? row.data1 ?? row.Data ?? row.data ?? data) as T;
};

const toActions = (value: unknown): ScreenAction[] =>
  Array.isArray(value)
    ? SCREEN_ACTIONS.filter((action) => value.map((v) => String(v).toUpperCase()).includes(action))
    : [];

const normalizeMatrix = (raw: Partial<ScreenAccessMatrix> | null | undefined, screen: ScreenKey): ScreenAccessMatrix => ({
  screen,
  label: String(raw?.label ?? screen),
  companyRefId: Number(raw?.companyRefId ?? 0),
  allActions: raw?.allActions?.length ? toActions(raw.allActions) : [...SCREEN_ACTIONS],
  roles: (Array.isArray(raw?.roles) ? raw.roles : []).map((role) => ({
    roleId: Number(role.roleId),
    roleName: String(role.roleName ?? role.roleId),
    actions: toActions(role.actions),
    defaultActions: toActions(role.defaultActions),
    locked: Boolean(role.locked),
  })),
  customized: Boolean(raw?.customized),
  modifiedBy: raw?.modifiedBy ?? null,
  modifiedDate: raw?.modifiedDate ?? null,
});

export const screenAccessApi = {
  /** The caller's own actions on a screen. Any signed-in user. */
  getMine: async (screen: ScreenKey, companyRefId: number): Promise<MyScreenAccess> => {
    const { data } = await api.get(API_ENDPOINTS.SCREEN_ACCESS.MINE(screen), {
      params: { companyRefId: Number(companyRefId) || 0 },
    });
    const raw = unwrapApi<Partial<MyScreenAccess> | null>(data);
    return {
      screen,
      roleId: raw?.roleId == null ? null : Number(raw.roleId),
      actions: toActions(raw?.actions),
    };
  },

  /** Every role's actions. Super Admin only on the server. */
  getMatrix: async (screen: ScreenKey, companyRefId: number): Promise<ScreenAccessMatrix> => {
    const { data } = await api.get(API_ENDPOINTS.SCREEN_ACCESS.RULES(screen), {
      params: { companyRefId: Number(companyRefId) || 0 },
    });
    return normalizeMatrix(unwrapApi<Partial<ScreenAccessMatrix> | null>(data), screen);
  },

  /** Super Admin only. Answers with the saved rules. */
  saveMatrix: async (screen: ScreenKey, companyRefId: number, roles: RoleAccessUpdate[]): Promise<ScreenAccessMatrix> => {
    const { data } = await api.put(
      API_ENDPOINTS.SCREEN_ACCESS.RULES(screen),
      { roles },
      { params: { companyRefId: Number(companyRefId) || 0 } }
    );
    return normalizeMatrix(unwrapApi<Partial<ScreenAccessMatrix> | null>(data), screen);
  },
};

export default screenAccessApi;
